import { useMemo } from 'react'

import { documentHasFileId, lienDocumentIsAttached } from '../../../../lib/docs'
import { isPatientCountySameAsPracticeCounty } from '../../../../lib/script'
import {
    getPracticeIntentNoticeDocuments,
    getLatestPracticeSatisfactionLetters,
} from '../../../../lib/lienIntentNotices'
import { sortLienItemsByTypeAndSourceChain } from '../../../../lib/lienAmended'

import classes from './LienDrawer.module.css'
import LienDrawerItem from './LienDrawerItem'
import { useSelector } from 'react-redux'
import { formatDateLocal, formatTimeLocal } from '../../../../lib/script'
import {
    formatCreatedOnHover,
    getLienCreatedDate,
    getLienDisplayDate,
    lienDisplayUsesRecordingDate,
} from '../../../../lib/lienDisplayDate'
import { getDisplayNameForLien } from '../../../../lib/misc'
import { isTestCaseForLien } from '../../../../lib/systemData'
import DisplayBoxRight from '../../../fragments/DisplayBoxRight'
import BodySimpleModalV3 from '../../../common/modal/BodySimpleModalV3'
import HoverWrapperInfo from '../../../wrappers/HoverWrapperInfo'
import ViewDocument from '../procodocs/documents/controls/ViewDocument'
import Button from '../../../common/Button'
import CertifiedMailNumber from './CertifiedMailNumber'



export default function LienDrawer({data, fullData, clinic}) {
    const activeCase = useSelector(state => state.case.activeCase)
    const userToken = useSelector(state => state.user.token)
    // console.log('fullData', fullData)
    const getItemKey = (item, index) => item?.id ?? item?.practice_package_uuid ?? item?.patient_package_uuid ?? `${item?.type || 'lien-item'}-${index}`
    const hasSameCounty = isPatientCountySameAsPracticeCounty(activeCase, clinic)

    const items = useMemo(() => (Array.isArray(data) ? data : []), [data])
    const intentItems = useMemo(
        () => items.filter((item) => item?.type === 'intent'),
        [items]
    )
    const intentNoticeDocuments = useMemo(
        () => getPracticeIntentNoticeDocuments(fullData),
        [fullData]
    )
    const showIntentTable = intentItems.length > 0 || intentNoticeDocuments.length > 0
    const nonIntentItems = useMemo(
        () => items.filter((item) => item?.type !== 'intent'),
        [items]
    )

    const hasLienSideValue = (value) =>
        value != null && `${value}`.trim() !== ''

    /** Both sides have stored county IDs and they are not the same county. */
    const hasDistinctStoredCounties = (item) => {
        if (!hasLienSideValue(item?.patient_county_id) || !hasLienSideValue(item?.practice_county_id)) {
            return false
        }

        return Number(item.patient_county_id) !== Number(item.practice_county_id)
    }

    // Same clerk package UUID collapses to one Practice table unless stored counties differ.
    const isSamePackage = (item) =>
        item?.patient_package_uuid != null &&
        item?.practice_package_uuid != null &&
        item.patient_package_uuid === item.practice_package_uuid &&
        !hasDistinctStoredCounties(item)

    // If patient_package_uuid and practice_package_uuid are the same (and counties are not
    // distinct), treat it as the same table (Practice). Same UUID + different county IDs → both tables.

    const hasPracticeCountyLienData = (item) =>
        hasLienSideValue(item?.practice_package_uuid) ||
        hasLienSideValue(item?.practice_lien_status) ||
        !!item?.practice_document ||
        hasLienSideValue(item?.practice_book) ||
        hasLienSideValue(item?.practice_page)

    const hasPatientCountyLienData = (item) =>
        hasLienSideValue(item?.patient_package_uuid) ||
        hasLienSideValue(item?.patient_lien_status) ||
        !!item?.patient_document ||
        hasLienSideValue(item?.patient_book) ||
        hasLienSideValue(item?.patient_page)

    const getSameCountyLienProps = (item) => {
        if (hasPatientCountyLienData(item)) {
            return {
                documentProp: 'patient_document',
                bookProp: 'patient_book',
                pageProp: 'patient_page',
                uuidProp: 'patient_package_uuid',
            }
        }

        return {
            documentProp: 'practice_document',
            bookProp: 'practice_book',
            pageProp: 'practice_page',
            uuidProp: 'practice_package_uuid',
        }
    }

    const practiceCountyItems = nonIntentItems.filter(item => {
        if (isSamePackage(item)) return true
        return hasPracticeCountyLienData(item)
    })

    const hasPracticeCountyData = practiceCountyItems.length > 0
    const hasPatientCountyData = nonIntentItems.some(
        (item) => !isSamePackage(item) && hasPatientCountyLienData(item)
    )
    // Show both tables whenever practice-side and patient-side filings exist
    // (including separate one-sided rows, not only dual-UUID on a single row).
    const hasBothLienSides = hasPracticeCountyData && hasPatientCountyData
    const shouldShowPatientLienTable = hasPatientCountyData

    const patientCountyItems = nonIntentItems.filter(item => {
        if (!shouldShowPatientLienTable) return false
        // Shared package + same/missing counties: only practice table (avoids two identical tables).
        // Shared package + distinct stored county IDs: include here so both tables show.
        if (isSamePackage(item)) return false
        return hasPatientCountyLienData(item)
    })

    const showPracticeLienTable = practiceCountyItems.length > 0
    const showPatientLienTable = shouldShowPatientLienTable && patientCountyItems.length > 0

    // Combined same-county table only when there is not both a practice and patient side to show.
    const useCombinedSameCountyTable = hasSameCounty && !hasBothLienSides

    const satisfactionLetters = useMemo(
        () => getLatestPracticeSatisfactionLetters(fullData),
        [fullData]
    )

    const renderDocumentView = (doc) => {
        if (!lienDocumentIsAttached(doc)) {
            return '-'
        }

        if (!documentHasFileId(doc)) {
            return (
                <HoverWrapperInfo modalText="Document not yet uploaded to Box">
                    <Button
                        btnStyle="btn_small_icon"
                        isDisabled
                        title="Document not yet uploaded to Box"
                    >
                        <i className="ti ti-eye" />
                    </Button>
                </HoverWrapperInfo>
            )
        }

        return (
            <HoverWrapperInfo modalText="View document">
                <ViewDocument
                    userToken={userToken}
                    activeCase={activeCase}
                    item={doc}
                    btnStyle="btn_small_icon"
                    docType="document"
                    title="View document"
                >
                    <i className="ti ti-eye" />
                </ViewDocument>
            </HoverWrapperInfo>
        )
    }

    const renderIntentNoticeRow = (docItem, docIndex, parentKey = 'intent') => (
        <tr key={`${parentKey}-notice-${docItem?.id ?? docItem?.name ?? docIndex}`}>
            <td style={{ paddingLeft: 20 }}>{docItem.name}</td>
            <td><CertifiedMailNumber value={docItem.certified_mail_number} /></td>
            <td className='table-cell-fit'>
                {isTestCaseForLien(activeCase) ? (
                    <DisplayBoxRight>{formatTimeLocal(docItem.created_at)}</DisplayBoxRight>
                ) : (
                    <DisplayBoxRight>{formatDateLocal(docItem.created_at)}</DisplayBoxRight>
                )}
            </td>
            <td>
                {renderDocumentView(docItem?.document)}
            </td>
        </tr>
    )

    const renderSatisfactionLetterRow = (
        docItem,
        docIndex,
        parentKey = 'satisfaction-letter',
        includeRecordingReferenceColumns = false
    ) => (
        <tr key={`${parentKey}-${docItem?.id ?? docItem?.document?.id ?? docItem?.name ?? docIndex}`}>
            <td style={includeRecordingReferenceColumns ? { paddingLeft: 20 } : undefined}>
                {docItem?.name || '-'}
            </td>
            <td>
                <CertifiedMailNumber value={docItem?.certified_mail_number} />
            </td>
            <td className='table-cell-fit'>
                {docItem?.created_at ? (
                    <DisplayBoxRight>{formatDateLocal(docItem.created_at)}</DisplayBoxRight>
                ) : (
                    '-'
                )}
            </td>
            {includeRecordingReferenceColumns && <td colSpan={2}></td>}
            <td>
                {renderDocumentView(docItem?.document)}
            </td>
        </tr>
    )

    return <div className={classes.container}>
        <div className={classes.container_inner}>
            {useCombinedSameCountyTable ? 
                <table className='table-border-radius-8'>
                    <thead>
                        <tr>
                            <th>Lien</th>
                            <th className='table-cell-fit'>Certified Mail Number</th>
                            <th>Date</th>
                            <th colSpan={2}>Recording Reference</th>
                            <th className='table-cell-fit'>View</th>
                        </tr>
                    </thead>
                    <tbody>
                    {sortLienItemsByTypeAndSourceChain(items).map((item, index) => {
                        const sameCountyLienProps = getSameCountyLienProps(item)

                        return (
                            <LienDrawerItem
                                key={getItemKey(item, index)}
                                item={item}
                                data={data}
                                fullData={fullData}
                                activeCase={activeCase}
                                clinic={clinic}
                                {...sameCountyLienProps}
                            />
                        )
                    })}
                    {intentItems.length === 0 && intentNoticeDocuments.map((docItem, docIndex) => (
                        <tr key={`orphan-notice-same-county-${docItem?.id ?? docItem?.name ?? docIndex}`}>
                            <td style={{ paddingLeft: 20 }}>{docItem.name}</td>
                            <td><CertifiedMailNumber value={docItem.certified_mail_number} /></td>
                            <td className='table-cell-fit'>
                                {isTestCaseForLien(activeCase) ? (
                                    <DisplayBoxRight>{formatTimeLocal(docItem.created_at)}</DisplayBoxRight>
                                ) : (
                                    <DisplayBoxRight>{formatDateLocal(docItem.created_at)}</DisplayBoxRight>
                                )}
                            </td>
                            <td colSpan={2}></td>
                            <td>
                                {renderDocumentView(docItem?.document)}
                            </td>
                        </tr>
                    ))}
                    {satisfactionLetters.map((docItem, docIndex) =>
                        renderSatisfactionLetterRow(docItem, docIndex, 'same-county-satisfaction-letter', true)
                    )}
                    </tbody>                    
                </table>                
                :
                <div>
                <div className={classes.blocks_container}>
                    {/* 1) Intent table (standalone) */}
                    {showIntentTable && <table className='table-border-radius-8'>
                        <thead>
                            <tr>
                                <th>Intent of Lien</th>
                                <th className='table-cell-fit'>Certified Mail Number</th>
                                <th>Date</th>
                                <th className='table-cell-fit'>View</th>
                            </tr>
                        </thead>
                        <tbody>
                            {intentItems.map((item, index) => (
                                <tr key={`intent-${getItemKey(item, index)}`}>
                                    <td>
                                        <BodySimpleModalV3 message={isTestCaseForLien(activeCase) && <>
                                            {item.practice_package_uuid && <div>practice uuid - <strong>{item.practice_package_uuid}</strong></div>}
                                            {item.patient_package_uuid && item.patient_package_uuid != item.practice_package_uuid && <div>patient uuid - <strong>{item.patient_package_uuid}</strong></div>}
                                        </>} disabled={!isTestCaseForLien(activeCase)}>
                                            {getDisplayNameForLien(item.type)}
                                        </BodySimpleModalV3>
                                    </td>
                                    <td>
                                        <CertifiedMailNumber value={item.certified_mail_number} />
                                    </td>
                                    <td className='table-cell-fit'>
                                        {isTestCaseForLien(activeCase) ? (
                                            <DisplayBoxRight>{formatTimeLocal(item.created_at)}</DisplayBoxRight>
                                        ) : (() => {
                                            const displayDate = getLienDisplayDate(item)
                                            if (!displayDate) {
                                                return <DisplayBoxRight></DisplayBoxRight>
                                            }
                                            const dateText = formatDateLocal(displayDate)
                                            const createdHover = lienDisplayUsesRecordingDate(item)
                                                ? formatCreatedOnHover(getLienCreatedDate(item), formatDateLocal)
                                                : null
                                            return (
                                                <DisplayBoxRight>
                                                    <span title={createdHover || undefined}>{dateText}</span>
                                                </DisplayBoxRight>
                                            )
                                        })()}
                                    </td>
                                    <td>
                                        {renderDocumentView(item?.patient_document)}
                                    </td>
                                </tr>
                            ))}
                            {intentNoticeDocuments.map((docItem, docIndex) =>
                                renderIntentNoticeRow(docItem, docIndex)
                            )}
                        </tbody>
                    </table>}

                    {/* 2) Practice lien table — omit when empty (e.g. patient-county-only single filing) */}
                    {showPracticeLienTable && <table className='table-border-radius-8'>
                        <thead>
                            <tr>
                                <th>Lien/Practice</th>
                                <th>Date</th>
                                <th>Book</th>
                                <th>Page</th>
                                <th className='table-cell-fit'>View</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortLienItemsByTypeAndSourceChain(practiceCountyItems, 'practice').map((item, index) => (
                                <LienDrawerItem
                                    key={`practice-county-${getItemKey(item, index)}`}
                                    item={item}
                                    data={data}
                                    fullData={fullData}
                                    activeCase={activeCase}
                                    clinic={clinic}
                                    mode='county'
                                    documentProp='practice_document'
                                    bookProp='practice_book'
                                    pageProp='practice_page'
                                    uuidProp='practice_package_uuid'
                                />
                            ))}
                        </tbody>
                    </table>}

                    {/* 3) Patient lien table — omit when empty (e.g. practice-county-only or single shared package) */}
                    {showPatientLienTable && <table className='table-border-radius-8'>
                        <thead>
                            <tr>
                                <th>Lien/Patient</th>
                                <th>Date</th>
                                <th>Book</th>
                                <th>Page</th>
                                <th className='table-cell-fit'>View</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortLienItemsByTypeAndSourceChain(patientCountyItems, 'patient').map((item, index) => (
                                <LienDrawerItem
                                    key={`patient-county-${getItemKey(item, index)}`}
                                    item={item}
                                    data={data}
                                    fullData={fullData}
                                    activeCase={activeCase}
                                    clinic={clinic}
                                    mode='county'
                                    documentProp='patient_document'
                                    bookProp='patient_book'
                                    pageProp='patient_page'
                                    uuidProp='patient_package_uuid'
                                />
                            ))}
                        </tbody>
                    </table>}
                </div>

                {satisfactionLetters.length > 0 && (
                    <div className={classes.satisfaction_container} style={{ marginTop: 16 }}>
                        <table className='table-border-radius-8' style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>Satisfaction Letters</th>
                                    <th className='table-cell-fit'>Certified Mail Number</th>
                                    <th>Date</th>
                                    <th className='table-cell-fit'>View</th>
                                </tr>
                            </thead>
                            <tbody>
                                {satisfactionLetters.map((docItem, idx) =>
                                    renderSatisfactionLetterRow(docItem, idx)
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
                </div>         
            }           

        </div>
    </div>
}